import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { formatApiError, geoApi, citiesApi } from '../lib/api';
import { Loader2, Eye, EyeOff, Check, MapPin, AlertTriangle } from 'lucide-react';

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });

  // Location state
  const [locationStatus, setLocationStatus] = useState('detecting'); // detecting | detected | failed
  const [detectedCity, setDetectedCity] = useState('');
  const [detectedCountry, setDetectedCountry] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [cities, setCities] = useState([]);

  useEffect(() => {
    detectLocation();
  }, []);

  const detectLocation = async () => {
    setLocationStatus('detecting');
    try {
      const response = await geoApi.detect();
      const data = response.data;

      if (data.detected_city || data.detected_country) {
        setLocationStatus('detected');
        setDetectedCity(data.detected_city || '');
        setDetectedCountry(data.is_canada ? 'Canada' : (data.detected_country || ''));
        if (data.suggested_city) {
          setSelectedCity(data.suggested_city);
        }
      } else {
        setLocationStatus('failed');
        loadCities();
      }
    } catch {
      setLocationStatus('failed');
      loadCities();
    }
  };

  const loadCities = async () => {
    try {
      const response = await citiesApi.getAll();
      setCities(response.data);
    } catch {
      setCities([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      await register(formData.email, formData.password, formData.name);
      navigate('/dashboard');
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4" data-testid="register-page">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="w-14 h-14 mx-auto mb-4">
            <img src="/logo.png" alt="HabeshaTown" className="w-full h-full object-contain" />
          </div>
          <CardTitle 
            className="text-2xl"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Create Account
          </CardTitle>
          <CardDescription>
            Join HabeshaTown marketplace today
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div 
                className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm"
                data-testid="register-error"
              >
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Your name"
                required
                data-testid="name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="your@email.com"
                required
                data-testid="email-input"
              />
            </div>

            {/* Location Detection */}
            <div className="space-y-2" data-testid="location-detection">
              <Label className="flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5" />
                Location
              </Label>

              {locationStatus === 'detecting' && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50 text-sm text-muted-foreground" data-testid="location-detecting">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Detecting your location...
                </div>
              )}

              {locationStatus === 'detected' && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 border border-green-200 text-sm text-green-700" data-testid="location-detected">
                  <Check className="h-4 w-4 flex-shrink-0" />
                  <span>
                    Location detected: <strong>{detectedCity ? `${detectedCity}, ` : ''}{detectedCountry}</strong>
                  </span>
                </div>
              )}

              {locationStatus === 'failed' && (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">
                    We couldn't detect your location. Please select your city:
                  </p>
                  <select
                    value={selectedCity}
                    onChange={(e) => setSelectedCity(e.target.value)}
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    data-testid="city-select"
                  >
                    <option value="">Select your city</option>
                    {cities.map((city) => (
                      <option key={city.id} value={city.id}>
                        {city.name}, {city.province}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  data-testid="toggle-password"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-xs text-muted-foreground">
                Must be at least 6 characters
              </p>
            </div>

            <Button 
              type="submit" 
              className="w-full" 
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Creating account...
                </>
              ) : (
                'Create Account'
              )}
            </Button>
          </form>

          {/* Benefits */}
          <div className="mt-6 pt-6 border-t border-border">
            <p className="text-sm font-medium mb-3">Free account includes:</p>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-primary" />
                5 free listings
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-primary" />
                Browse unlimited listings
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-primary" />
                Contact sellers directly
              </li>
            </ul>
          </div>

          <div className="mt-6 text-center text-sm">
            <span className="text-muted-foreground">Already have an account? </span>
            <Link 
              to="/login" 
              className="text-primary hover:text-primary/80 font-medium"
              data-testid="login-link"
            >
              Sign in
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
